import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, NavLink, useLocation } from 'react-router-dom'

import AddBankAccount from '../../components/Modal/AddBankAcc'
import ChartAccountModal from '../../components/Modal/ChartOfAccount/ChartAccountModal'
import SAlert from '../../components/SAlert'
import { MENUS, REVREX_APP_BASE_URL, URLS } from '../../resources/constants'
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook'
import {
  getAccountSettings,
  getThirdPartyImportedStep,
  getThirdPartyStepsCompleted,
} from '../../store/settings/selectors'
import { getAccessToken, getBranch } from '../../store/user/selectors'

const Footer: FC = () => {
  const { t } = useTranslation('layout.header')
  const location = useLocation()
  const { fetchAccountSettingsData } = useSettingsApi()

  const accessToken = useSelector(getAccessToken)
  const branch = useSelector(getBranch)
  const accountSettings = useSelector(getAccountSettings)
  const thirdPartyStepsCompleted = useSelector(getThirdPartyStepsCompleted)
  const thirdPartyImportedStep = useSelector(getThirdPartyImportedStep)

  const [showModal, setShowModal] = useState(false)
  const [showQBFlowModal, setShowQBFlowModal] = useState(false)
  const [showCOAModal, setShowCOAModal] = useState(false)
  const [isNewAccount, setIsNewAccount] = useState(false)

  const currentPathKey = location.pathname.split('/')[1]
  const currentYear = new Date().getFullYear()

  useEffect(() => {
    if (branch?.id && accessToken) {
      fetchAccountSettingsData(
        { branch_id: Number(branch.id) },
        () => {},
        () => {}
      )
    }
  }, [])

  // Reminder: If you modify the menu structure here, please update the Header component accordingly.
  const menuList = useMemo(() => {
    const isClassificationVisible =
      accountSettings?.transaction_activity_status >= 3 &&
      [0, 5].includes(thirdPartyStepsCompleted)

    const baseMenu = [
      {
        title: t('header.menu.dashboard'),
        url: URLS.DASHBOARD,
        isActive: MENUS.DASHBOARD.includes(currentPathKey),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M2.50048 10.8334H3.33382V12.5V16.6667C3.33382 17.5859 4.08132 18.3334 5.00048 18.3334H15.0005C15.9196 18.3334 16.6671 17.5859 16.6671 16.6667V12.5V10.8334H17.5005C17.8371 10.8334 18.1421 10.63 18.2705 10.3192C18.3996 10.0075 18.328 9.64921 18.0896 9.41088L10.5896 1.91088C10.2638 1.58505 9.73715 1.58505 9.41132 1.91088L1.91132 9.41088C1.67298 9.64921 1.60132 10.0075 1.73048 10.3192C1.85965 10.63 2.16382 10.8334 2.50048 10.8334ZM10.0005 3.67838L15.0005 8.67837V12.5L15.0013 16.6667H5.00048V12.5V10V8.67921L10.0005 3.67838Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.0003 14.1667C11.8387 14.1667 13.3337 12.6717 13.3337 10.8333C13.3337 8.995 11.8387 7.5 10.0003 7.5C8.16199 7.5 6.66699 8.995 6.66699 10.8333C6.66699 12.6717 8.16199 14.1667 10.0003 14.1667ZM10.0003 9.16667C10.9195 9.16667 11.667 9.91417 11.667 10.8333C11.667 11.7525 10.9195 12.5 10.0003 12.5C9.08116 12.5 8.33366 11.7525 8.33366 10.8333C8.33366 9.91417 9.08116 9.16667 10.0003 9.16667Z"
              fill="white"
            />
          </svg>
        ),
      },
    ]

    if (!isClassificationVisible) return baseMenu

    return [
      ...baseMenu,
      {
        title: t('header.menu.revrex_app'),
        type: 'action',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="SVGRepo_bgCarrier" stroke-width="0" />
            <g
              id="SVGRepo_tracerCarrier"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <g id="SVGRepo_iconCarrier">
              {' '}
              <g id="Interface / External_Link">
                {' '}
                <path
                  id="Vector"
                  d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M20 9V4M20 4H15M20 4L13 11"
                  stroke="#fff"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />{' '}
              </g>{' '}
            </g>
          </svg>
        ),
        onClick: () => {
          accountSettings.bank_account_count < 1
            ? setShowModal(true)
            : (window.location.href = REVREX_APP_BASE_URL || '')
        },
      },
      {
        title: t('header.menu.classification'),
        isDropdown: true,
        isActive: MENUS.Classification.includes(currentPathKey),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M21 9L17 5V8H10V10H17V13M7 11L3 15L7 19V16H14V14H7V11Z"
              fill="white"
            />
          </svg>
        ),
        subMenu: [
          {
            title: t('header.menu.classification.deposit'),
            url: URLS.DEPOSIT_GL_CLASSIFICATION_STATUS,
          },
          {
            title: t('header.menu.classification.expense'),
            url: URLS.EXPENSE_GL_CLASSIFICATION_STATUS,
          },
          {
            title: t('header.menu.classification.revenue'),
            url: URLS.REVENUE_GL_CLASSIFICATION_STATUS,
          },
          {
            title: t('header.menu.classification.credit_card'),
            url: URLS.CREDIT_CARD_GL_CLASSIFICATION_STATUS,
          },
          {
            title: t('header.menu.classification.reconcilation'),
            url: URLS.BANK_RECONCILE,
          },
        ],
      },
      {
        title: t('header.menu.maintainance'),
        isDropdown: true,
        isActive: MENUS.CHART_OF_ACCOUNT.includes(currentPathKey),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <g clip-path="url(#clip0_472_4567)">
              <path
                d="M7.50117 2.49721C7.49141 2.14877 7.81602 1.84916 8.16133 1.87455C9.40039 1.87611 10.6398 1.87299 11.8793 1.87611C12.2094 1.86869 12.5039 2.16557 12.4984 2.49486C12.5008 3.7476 12.5012 5.00111 12.4984 6.25385C12.5059 6.58471 12.2082 6.88119 11.8777 6.87338C11.4602 6.87846 11.0426 6.87299 10.625 6.87494C10.625 7.70815 10.625 8.54174 10.625 9.37494C12.9176 9.37572 15.2102 9.37338 17.5027 9.37611C17.8336 9.36752 18.1289 9.66557 18.1238 9.99525C18.127 11.0386 18.1238 12.0816 18.125 13.1249C18.5027 13.1246 18.8809 13.1261 19.2586 13.1238C19.3957 13.1234 19.5395 13.1265 19.6625 13.1953C19.8441 13.2882 19.9621 13.4734 20 13.6706V17.5535C19.9723 17.8644 19.6937 18.1304 19.3781 18.1234C18.1793 18.1281 16.9805 18.1218 15.7816 18.1265C15.6227 18.1265 15.452 18.1288 15.3133 18.0394C15.1098 17.9249 14.9875 17.6941 14.9996 17.4617C15 16.2371 15 15.0128 14.9996 13.7882C14.9707 13.4253 15.3 13.096 15.6629 13.1246C16.0668 13.1242 16.4711 13.1257 16.875 13.1249C16.875 12.2917 16.875 11.4581 16.875 10.6249C14.7918 10.6249 12.7082 10.6249 10.625 10.6249C10.625 11.4581 10.625 12.2917 10.625 13.1249C11.0559 13.1281 11.4871 13.1187 11.918 13.1285C12.2352 13.1394 12.5043 13.4328 12.4988 13.748C12.5027 14.9472 12.4973 16.146 12.5016 17.3453C12.5 17.4839 12.5051 17.6304 12.4434 17.7589C12.3551 17.948 12.1688 18.0953 11.9586 18.1167C11.8406 18.1296 11.7215 18.1257 11.6031 18.1257C10.443 18.123 9.28242 18.1277 8.12227 18.1234C7.82969 18.1281 7.55391 17.8968 7.51055 17.6074C7.49375 17.4808 7.49961 17.3535 7.49961 17.2265C7.50156 16.0667 7.49766 14.907 7.50117 13.7472C7.49375 13.4312 7.76289 13.1402 8.07969 13.1288C8.51133 13.1187 8.94336 13.1285 9.375 13.1249C9.375 12.2917 9.375 11.4581 9.375 10.6249C7.2918 10.6249 5.2082 10.6249 3.125 10.6249C3.125 11.4581 3.125 12.2917 3.125 13.1249C3.55586 13.1281 3.98711 13.1187 4.41797 13.1285C4.73437 13.1394 5.00352 13.4312 4.99883 13.746C5.00195 14.9843 4.99883 16.223 5 17.4613C5.01211 17.6945 4.88906 17.9261 4.68516 18.0413C4.53398 18.1359 4.34883 18.1253 4.17773 18.1261C2.99336 18.1222 1.80898 18.1277 0.624609 18.1238C0.316797 18.1304 0.0460937 17.88 0 17.5804V13.68C0.0375 13.3581 0.338672 13.1011 0.663672 13.1246C1.06758 13.1242 1.47109 13.1257 1.875 13.1249C1.87461 12.1609 1.87617 11.1968 1.87422 10.2328C1.875 10.0499 1.85781 9.85229 1.95781 9.689C2.07187 9.48315 2.30664 9.36283 2.53984 9.37455C4.81836 9.37533 7.09648 9.37494 9.375 9.37494C9.375 8.54174 9.375 7.70815 9.375 6.87494C8.95781 6.87299 8.54023 6.87807 8.12305 6.87338C7.7918 6.8808 7.49219 6.58354 7.50117 6.2515C7.49922 4.99994 7.49922 3.74838 7.50117 2.49721Z"
                fill="white"
              />
            </g>
          </svg>
        ),
        subMenu: [
          {
            title: t('header.menu.maintainance.manage_chart_of_accounts'),
            url: URLS.CHARTACCOUNT,
          },
          {
            title: t('header.menu.maintainance.change_existing_account'),
            type: 'action',
            onClick: () => {
              setIsNewAccount(false)
              setShowCOAModal(true)
            },
          },
          {
            title: t('header.menu.maintainance.add_new_account'),
            type: 'action',
            onClick: () => {
              setIsNewAccount(true)
              setShowCOAModal(true)
            },
          },
          {
            title: t('header.menu.maintainance.transaction_register'),
            url: URLS.VIEW_REGISTER,
          },
          {
            title: t('header.menu.maintainance.budget_entry'),
            url: URLS.BUDGET,
          },
          ...(accountSettings?.is_coa_modified === 1 &&
          (thirdPartyStepsCompleted === 0 || thirdPartyStepsCompleted > 2)
            ? [
                {
                  title: t('header.menu.maintainance.beginning_balance_entry'),
                  url: URLS.OPENNING_BALANCE,
                },
              ]
            : []),
          {
            title: t('header.menu.maintainance.qbo_vendors'),
            url: URLS.QBO_VENDORS,
          },
          {
            title: t('header.menu.maintainance.qbo_customers'),
            url: URLS.QBO_CUSTOMERS,
          },
        ],
      },
      {
        title: t('header.menu.forms'),
        isDropdown: true,
        isActive: MENUS.FORMS.includes(currentPathKey),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M7.29892 0.833252H12.7011C13.3719 0.833242 13.9255 0.833234 14.3765 0.870081C14.8449 0.908352 15.2755 0.990489 15.68 1.19657C16.3072 1.51614 16.8171 2.02608 17.1367 2.65329C17.3428 3.05773 17.4249 3.48832 17.4632 3.95673C17.5 4.40772 17.5 4.96135 17.5 5.63215V9.97846C17.5 10.4365 17.5 10.6655 17.4468 10.7604C17.3832 10.8739 17.3583 10.8957 17.2374 10.9438C17.1363 10.984 16.8127 10.9411 16.1656 10.8551C16.0569 10.8407 15.946 10.8333 15.8333 10.8333C14.4526 10.8333 13.3333 11.9525 13.3333 13.3333C12.6935 13.3333 12.0537 13.5773 11.5656 14.0655C10.5893 15.0418 10.5893 16.6247 11.5656 17.601L11.9973 18.0328C12.3527 18.3881 12.5304 18.5658 12.5424 18.719C12.5529 18.8518 12.499 18.982 12.3978 19.0685C12.2809 19.1684 12.0299 19.1685 11.5279 19.1687C10.7503 19.1691 9.9727 19.1684 9.19512 19.1677C8.56304 19.1671 7.93096 19.1666 7.2989 19.1666C6.6281 19.1666 6.07447 19.1666 5.62348 19.1298C5.15507 19.0915 4.72448 19.0094 4.32003 18.8033C3.69283 18.4837 3.18289 17.9738 2.86331 17.3466C2.65724 16.9421 2.5751 16.5115 2.53683 16.0431C2.49998 15.5921 2.49999 15.0385 2.5 14.3677V5.63216C2.49999 4.96136 2.49998 4.40772 2.53683 3.95673C2.5751 3.48832 2.65724 3.05773 2.86331 2.65329C3.18289 2.02608 3.69283 1.51614 4.32003 1.19657C4.72448 0.990489 5.15507 0.908352 5.62348 0.870081C6.07447 0.833234 6.62812 0.833242 7.29892 0.833252ZM5.83333 4.16659C5.3731 4.16659 5 4.53968 5 4.99992C5 5.46016 5.3731 5.83325 5.83333 5.83325H13.3333C13.7936 5.83325 14.1667 5.46016 14.1667 4.99992C14.1667 4.53968 13.7936 4.16659 13.3333 4.16659H5.83333ZM5 8.33325C5 7.87301 5.3731 7.49992 5.83333 7.49992H10.8333C11.2936 7.49992 11.6667 7.87301 11.6667 8.33325C11.6667 8.79349 11.2936 9.16659 10.8333 9.16659H5.83333C5.3731 9.16659 5 8.79349 5 8.33325ZM5 11.6666C5 11.2063 5.3731 10.8333 5.83333 10.8333H7.5C7.96024 10.8333 8.33333 11.2063 8.33333 11.6666C8.33333 12.1268 7.96024 12.4999 7.5 12.4999H5.83333C5.3731 12.4999 5 12.1268 5 11.6666Z"
              fill="white"
            />
            <path
              d="M15.8333 12.4999C16.2936 12.4999 16.6667 12.873 16.6667 13.3333V16.3214L17.7441 15.244C18.0695 14.9186 18.5972 14.9186 18.9226 15.244C19.248 15.5694 19.248 16.0971 18.9226 16.4225L16.4226 18.9225C16.2663 19.0788 16.0543 19.1666 15.8333 19.1666C15.6123 19.1666 15.4004 19.0788 15.2441 18.9225L12.7441 16.4225C12.4186 16.0971 12.4186 15.5694 12.7441 15.244C13.0695 14.9186 13.5972 14.9186 13.9226 15.244L15 16.3214V13.3333C15 12.873 15.3731 12.4999 15.8333 12.4999Z"
              fill="white"
            />
          </svg>
        ),
        subMenu: [
          // { title: t('header.menu.forms.433_b'), url: URLS.FOURTHREETHREEB },
          {
            title: t('header.menu.forms.personal_income_statement'),
            url: URLS.PERSONAL_INCOME_STATMENT,
          },
          {
            title: t('header.menu.forms.personal_balance_sheet'),
            url: URLS.PERSONAL_BALANCE_SHEET,
          },
        ],
      },
      {
        title: t('header.menu.reports'),
        isDropdown: true,
        isActive: MENUS.REPORTS.includes(currentPathKey),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M16.5855 7.15508C16.5447 7.06675 16.4938 6.98258 16.4222 6.91091L11.4222 1.91091C11.3505 1.83925 11.2663 1.78841 11.178 1.74758C11.153 1.73591 11.1263 1.72925 11.0997 1.72008C11.0297 1.69675 10.958 1.68175 10.8838 1.67758C10.8663 1.67591 10.8505 1.66675 10.833 1.66675H4.99967C4.08051 1.66675 3.33301 2.41425 3.33301 3.33341V16.6667C3.33301 17.5859 4.08051 18.3334 4.99967 18.3334H14.9997C15.9188 18.3334 16.6663 17.5859 16.6663 16.6667V7.50008C16.6663 7.48258 16.6572 7.46675 16.6555 7.44841C16.6513 7.37425 16.6372 7.30258 16.613 7.23258C16.6047 7.20591 16.5972 7.18008 16.5855 7.15508ZM13.8213 6.66675H11.6663V4.51175L13.8213 6.66675ZM4.99967 16.6667V3.33341H9.99967V7.50008C9.99967 7.96091 10.3722 8.33341 10.833 8.33341H14.9997L15.0013 16.6667H4.99967Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.66699 10.0001H13.3337V11.6667H6.66699V10.0001ZM6.66699 13.3334H13.3337V15.0001H6.66699V13.3334ZM6.66699 6.66675H8.33366V8.33341H6.66699V6.66675Z"
              fill="white"
            />
          </svg>
        ),
        subMenu: [
          {
            title: t('header.menu.reports.transactions'),
            children: [
              {
                title: t(
                  'header.menu.reports.transactions.bank_transactions_history'
                ),
                url: URLS.BANK_TRANSACTION,
              },
              {
                title: t(
                  'header.menu.reports.transactions.sales_transactions_history'
                ),
                url: URLS.SALES_TRANSACTION,
              },
            ],
          },

          {
            title: t('header.menu.reports.balance_sheet'),
            url: URLS.BALANCESHEET,
          },
          {
            title: t('header.menu.reports.statement_of_income'),
            url: URLS.PROFITLOSS,
          },
          {
            title: t('header.menu.reports.business_ratios'),
            url: URLS.BUSINESS_RATIO,
          },
        ],
      },
    ]
  }, [t, currentPathKey, accountSettings, thirdPartyStepsCompleted])

  const handleBankAddedSuccess = () => {
    if (branch?.id && accessToken) {
      fetchAccountSettingsData(
        { branch_id: Number(branch.id) },
        () => {},
        () => {}
      )
    }
    setShowModal(false)
  }

  return (
    <footer id="ux_page_footer">
      <div className="mob-menu-wrapper">
        <ul className="mob-menu d-flex">
          {menuList.map((menu: any, idx: number) => (
            <li
              key={idx}
              className={`nav-item ${
                menu.isDropdown ? 'dropdown' : 'nav-item-mob'
              }`}
            >
              <NavLink
                to={menu.url || '#'}
                style={{
                  background: menu.isActive ? '#185cb5' : '',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  borderTopLeftRadius: menu.isActive ? '15px' : '',
                  borderTopRightRadius: menu.isActive ? '15px' : '',
                }}
                onClick={menu.onClick}
                data-bs-toggle={menu.isDropdown ? 'dropdown' : undefined}
              >
                {menu.icon} {menu.title}
              </NavLink>

              {menu.isDropdown && (
                <ul className="dropdown-menu top-dropdown">
                  {menu.subMenu?.map((sub: any, sIdx: number) => {
                    if (sub.children) {
                      return (
                        <li key={sIdx} className="dropdown-submenu">
                          <Link
                            className="dropdown-item"
                            to={'#'}
                            style={{
                              alignItems: 'start',
                            }}
                          >
                            {sub.title || sub.titile}
                          </Link>
                          <div className="submenu-inner">
                            {sub.children.map((child: any, cIdx: number) => (
                              <li key={cIdx}>
                                <Link
                                  className="dropdown-item"
                                  to={child.url || '#'}
                                  style={{
                                    alignItems: 'start',
                                  }}
                                >
                                  {child.title}
                                </Link>
                              </li>
                            ))}
                          </div>
                        </li>
                      )
                    }
                    if (sub.type === 'action') {
                      return (
                        <li key={sIdx}>
                          <Link
                            className="dropdown-item"
                            to="#"
                            onClick={sub.onClick}
                            style={{
                              alignItems: 'start',
                            }}
                          >
                            {sub.title}
                          </Link>
                        </li>
                      )
                    }
                    return (
                      <li key={sIdx}>
                        <Link
                          className="dropdown-item"
                          to={sub.url || '#'}
                          style={{
                            alignItems: 'start',
                          }}
                        >
                          {sub.title}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>

      {showCOAModal && (
        <ChartAccountModal
          closeModal={() => setShowCOAModal(false)}
          addRecord={isNewAccount}
          record={null}
        />
      )}

      {showModal && (
        <AddBankAccount
          isShow={showModal}
          onHide={() => setShowModal(false)}
          onSuccess={handleBankAddedSuccess}
          branch_id={branch?.id}
        />
      )}

      <SAlert
        HtmlMsg={t('alert.msg', { url: REVREX_APP_BASE_URL })}
        show={showQBFlowModal}
        icon="success"
        title={t('alert.title')}
        confirmBtnText={t('alert.close.title')}
        onConfirm={() => setShowQBFlowModal(false)}
        showCancel={false}
        onCancel={() => setShowQBFlowModal(false)}
      />

      <div className="footer mt-2">
        <div className="container text-center">
          <p>© {currentYear} RevRex. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
